import loadable from '@loadable/component';
import { DateRange, Settings } from '@material-ui/icons';

import { BaseLayout, EmptyLayout, InformationLayout } from '@modules/layout/templates';
import { UserGrantActionToken, UserGrantCategoryToken, UserRole } from '@modules/types/graphql';

import type { RedirectProps, RouteComponentProps } from '@reach/router';
import type { LoadableComponent } from '@loadable/component';

export enum RouteMenuLocationEnum {
    sidebar = 'sidebar',
}

export enum RouteMenuSectionEnum {
    lapDataOriented = 'lapDataOriented',
    regionalCenterDataOriented = 'regionalCenterDataOriented',
}

export type OriginRoute = {
    name: string;
    title: string;
    path: string;
    private: boolean;
    LayoutComponent: React.FunctionComponent<any>;
    componentPath?: string;
    renderForPath?: Partial<Record<UserRole, string>>;
    redirectTo?: string;
    withBackIcon?: boolean;
    default?: boolean;
    actionGrants?: UserGrantActionToken[];
    categoryGrant?: UserGrantCategoryToken;
    categoryGrantFor?: Partial<Record<UserRole, UserGrantCategoryToken>>;
    roles?: UserRole[];
    titleFor?: Partial<Record<UserRole, string>>;
    menuTitle?: string;
    menuTitleFor?: Partial<Record<UserRole, string>>;
    menuLocations?: RouteMenuLocationEnum[];
    menuSections?: RouteMenuSectionEnum[];
    menuIcon?: React.FunctionComponent<any>;
    children?: React.ReactNode;
};

export type Route = Omit<OriginRoute, 'renderForPath' | 'componentPath'> & {
    renderFor?: Partial<Record<UserRole, LoadableComponent<any>>>;
    Component?: LoadableComponent<any>;
};

export type OriginRoutes = Record<string, OriginRoute>;
export type ConfigRoutes = Record<string, Route>;

export type RedirectRoute = RouteComponentProps<RedirectProps<{}>> & {
    name: string;
};

const originRoutes: OriginRoutes = {
    index: {
        name: 'index',
        title: '',
        path: '/',
        private: true,
        componentPath: '@pages',
        LayoutComponent: EmptyLayout,
    },
    booking: {
        name: 'booking.index',
        title: 'common:menu.booking',
        path: '/booking',
        private: true,
        categoryGrant: UserGrantCategoryToken.bookingSlot,
        menuLocations: [RouteMenuLocationEnum.sidebar],
        menuSections: [RouteMenuSectionEnum.regionalCenterDataOriented],
        menuIcon: DateRange,
        componentPath: '@pages/booking',
        LayoutComponent: BaseLayout,
    },
    bookingSingle: {
        name: 'booking.single',
        title: 'common:menu.booking',
        path: '/booking/:id',
        private: true,
        withBackIcon: true,
        categoryGrant: UserGrantCategoryToken.bookingAppointment,
        componentPath: '@pages/booking/single',
        LayoutComponent: BaseLayout,
    },

    settings: {
        name: 'settings.index',
        title: 'common:menu.settings',
        path: '/settings',
        private: true,
        roles: [UserRole.superAdmin, UserRole.admin],
        menuLocations: [RouteMenuLocationEnum.sidebar],
        menuSections: [RouteMenuSectionEnum.regionalCenterDataOriented],
        menuIcon: Settings,
        componentPath: '@pages/settings',
        LayoutComponent: BaseLayout,
    },

    profile: {
        name: 'profile.index',
        title: 'common:menu.profile',
        path: '/profile',
        private: true,
        categoryGrant: UserGrantCategoryToken.profile,
        categoryGrantFor: {
            [UserRole.school]: UserGrantCategoryToken.school,
        },
        componentPath: '@pages/profile',
        renderForPath: {
            [UserRole.school]: '@pages/profile/school',
        },
        LayoutComponent: BaseLayout,
    },

    profileEdit: {
        name: 'profile.edit',
        title: 'common:menu.profile',
        path: '/profile/edit',
        private: true,
        withBackIcon: true,
        categoryGrantFor: {
            [UserRole.school]: UserGrantCategoryToken.school,
        },
        componentPath: '@pages/profile',
        renderForPath: {
            [UserRole.school]: '@pages/profile/school/edit',
        },
        LayoutComponent: BaseLayout,
    },

    error: {
        name: 'error',
        title: 'Error Page',
        path: '/error',
        private: false,
        componentPath: '@pages/error', // TODO: Create Error page
        LayoutComponent: InformationLayout,
    },
};

const routes = Object.entries(originRoutes).reduce((carry, [key, route]) => {
    let asyncComponent: Route['Component'] = undefined;
    let renderFor: Route['renderFor'] = undefined;

    if (route.componentPath) {
        const path = route.componentPath.replace('@', '');

        asyncComponent = loadable(() => import(/* webpackPrefetch: true */ `../${path}`));
    }

    if (route.renderForPath) {
        renderFor = Object.entries(route.renderForPath).reduce((carry, [role, componentPath]) => {
            if (componentPath) {
                const path = componentPath.replace('@', '');
                const asyncComponent = loadable(
                    () => import(/* webpackPrefetch: true */ `../${path}`),
                );

                return { ...carry, [role]: asyncComponent };
            }

            return carry;
        }, {});
    }

    return {
        ...carry,
        [key]: { ...route, renderFor, Component: asyncComponent },
    };
}, {} as ConfigRoutes);

const redirects: RedirectRoute[] = [
    {
        name: 'default',
        from: '/:lang',
        to: routes.index.path,
        default: true,
    },
];

export { routes, redirects };
